import type { DrawingImage } from "@mnk/shared/models/drawingImage";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { useMemo, useState } from "react";

const IC_JCT_KP_SENTINEL = 10_000_000;

const parsePageRange = (text: string, total: number): { pages: number[]; isError: boolean } => {
  const normalized = text.replace(/\s/g, "");
  if (!normalized) return { pages: [], isError: false };
  if (!/^(\d+(-\d+)?)(,(\d+(-\d+)?))*$/.test(normalized)) {
    return { pages: [], isError: true };
  }
  const pages = new Set<number>();
  for (const part of normalized.split(",")) {
    if (!part) continue;
    const rangeMatch = part.match(/^(\d+)-(\d+)$/);
    if (rangeMatch) {
      const start = Number.parseInt(rangeMatch[1]);
      const end = Number.parseInt(rangeMatch[2]);
      if (start > end || start < 1 || end > total) return { pages: [], isError: true };
      for (let i = start; i <= end; i++) pages.add(i);
    } else {
      const page = Number.parseInt(part);
      if (page < 1 || page > total) return { pages: [], isError: true };
      pages.add(page);
    }
  }
  return { pages: Array.from(pages).sort((a, b) => a - b), isError: false };
};

interface Props {
  drawingImages: DrawingImage[];
  onDownload: (filteredImages: DrawingImage[]) => void;
  onCancel: () => void;
}

export const DownloadModal = ({ drawingImages, onDownload, onCancel }: Props) => {
  const total = drawingImages.length;
  const [mode, setMode] = useState<"all" | "page" | "kp">("all");
  const [pageRangeText, setPageRangeText] = useState("");
  const [pageSubmitted, setPageSubmitted] = useState(false);

  const handleModeChange = (newMode: "all" | "page" | "kp") => {
    setMode(newMode);
    setPageSubmitted(false);
  };

  const { minKp, maxKp } = useMemo(() => {
    const regular = drawingImages.filter((di) => di.startKiloPost < IC_JCT_KP_SENTINEL);
    if (regular.length === 0) return { minKp: 0, maxKp: 0 };
    return {
      minKp: Math.min(...regular.map((di) => di.startKiloPost)),
      maxKp: Math.max(...regular.map((di) => di.endKiloPost)),
    };
  }, [drawingImages]);

  const [kpStartText, setKpStartText] = useState(minKp.toFixed(2));
  const [kpEndText, setKpEndText] = useState(maxKp.toFixed(2));
  const kpStart = Number(kpStartText);
  const kpEnd = Number(kpEndText);

  const pageParseResult = useMemo(() => parsePageRange(pageRangeText, total), [pageRangeText, total]);

  const kpFilteredImages = useMemo(
    () =>
      drawingImages.filter((di) => {
        if (di.startKiloPost >= IC_JCT_KP_SENTINEL) return false;
        const start = Number(di.startKiloPost.toFixed(2));
        const end = Number(di.endKiloPost.toFixed(2));
        return start <= kpEnd && end >= kpStart;
      }),
    [drawingImages, kpStart, kpEnd],
  );

  const isValid = useMemo(() => {
    if (mode === "all") return true;
    if (mode === "page") return pageRangeText.length > 0;
    return kpStart <= kpEnd && kpFilteredImages.length > 0;
  }, [mode, pageRangeText, kpStart, kpEnd, kpFilteredImages]);

  const handleDownload = () => {
    if (mode === "page") {
      if (pageParseResult.isError) {
        setPageSubmitted(true);
        return;
      }
    }
    onDownload(getFilteredImages());
  };

  const getFilteredImages = (): DrawingImage[] => {
    if (mode === "all") return drawingImages;
    if (mode === "page") {
      return pageParseResult.pages.map((p) => drawingImages[p - 1]).filter(Boolean) as DrawingImage[];
    }
    return kpFilteredImages;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 w-full max-w-lg shadow-xl relative">
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 text-xl"
        >
          ✕
        </button>

        <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
          <DownloadIcon className="w-5 h-5" /> ダウンロード範囲の指定
        </h2>

        <div className="bg-gray-100 rounded-lg px-4 py-3 text-sm text-gray-500 mb-6">
          すべてのダウンロードに、表紙と数量集計表が含まれます
        </div>

        <div className="space-y-5">
          {/* 全ページ */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="mode"
              value="all"
              checked={mode === "all"}
              onChange={() => handleModeChange("all")}
              className="mt-1"
            />
            <div>
              <div className="flex items-center gap-1">
                <ArticleOutlinedIcon className="w-4 h-4" /> 全ページダウンロード
              </div>
              <div className="text-sm text-gray-500">すべてのページ（1〜{total}ページ）をダウンロードします</div>
            </div>
          </label>

          {/* ページ指定 */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="mode"
              value="page"
              checked={mode === "page"}
              onChange={() => handleModeChange("page")}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <ArticleOutlinedIcon className="w-4 h-4" /> ページ指定
              </div>
              <div className="mt-3 space-y-2">
                <input
                  type="text"
                  placeholder="例: 2,5-8,12"
                  value={pageRangeText}
                  onChange={(e) => setPageRangeText(e.target.value)}
                  className="border rounded-lg px-3 py-2 w-full text-sm"
                />
                {pageSubmitted && pageParseResult.isError && (
                  <div className="text-xs text-red-500">正しい形式で入力してください（例: 2,5-8,12）</div>
                )}
                <div className="text-xs text-gray-400">
                  ページ番号またはページ範囲をカンマで区切って入力してください
                </div>
              </div>
            </div>
          </label>

          {/* KP指定 */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="mode"
              value="kp"
              checked={mode === "kp"}
              onChange={() => handleModeChange("kp")}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <LocationOnOutlinedIcon className="w-4 h-4" /> KP指定
              </div>
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    開始 KP:
                    <input
                      type="number"
                      step="0.01"
                      value={kpStartText}
                      onChange={(e) => setKpStartText(e.target.value)}
                      className="border rounded-lg px-3 py-2 w-24 text-center"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    終了 KP:
                    <input
                      type="number"
                      step="0.01"
                      value={kpEndText}
                      onChange={(e) => setKpEndText(e.target.value)}
                      className="border rounded-lg px-3 py-2 w-24 text-center"
                    />
                  </label>
                </div>
                {mode === "kp" && kpStart > kpEnd && (
                  <div className="text-xs text-red-500">開始KPは終了KP以下にしてください</div>
                )}
                {mode === "kp" && kpStart <= kpEnd && kpFilteredImages.length === 0 && (
                  <div className="text-xs text-red-500">指定したKP範囲に該当するページがありません</div>
                )}
                <div className="text-xs text-gray-400">
                  指定したKP範囲に含まれるページをダウンロードします（IC/JCTは除外）
                </div>
              </div>
            </div>
          </label>
        </div>

        <div className="flex gap-3 mt-8">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 border border-gray-300 rounded-full py-3 text-gray-700 hover:bg-gray-50"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!isValid}
            className={`flex-1 rounded-full py-3 font-medium flex items-center justify-center gap-2 ${
              isValid ? "bg-black text-white hover:bg-gray-800" : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            <DownloadIcon className="w-4 h-4" /> ダウンロード
          </button>
        </div>
      </div>
    </div>
  );
};
